import type { FeatureGroup } from './types';
import { FadeIn } from './FadeIn';
import { MajorFeatureCard, MinorFeatureRow } from './FeatureCard';

export function FeatureSection({ group }: { group: FeatureGroup }) {
  const major = group.items.filter((i) => i.tier === 'major');
  const minor = group.items.filter((i) => i.tier === 'minor');

  return (
    <section id={group.id} className="scroll-mt-8 py-16">
      {/* Section header */}
      <FadeIn delay={0}>
        <div className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <div
              className={`h-1 w-8 rounded-full bg-gradient-to-r ${group.accentClass}`}
            />
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {group.label}
            </p>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {group.headline}
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {group.summary}
          </p>
        </div>
      </FadeIn>

      {/* Major features — full cards */}
      <div className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {major.map((item, i) => (
          <FadeIn
            key={item.title}
            delay={Math.min(i * 0.05, 0.3)}
            className="h-full"
          >
            <MajorFeatureCard {...item} />
          </FadeIn>
        ))}
      </div>

      {/* Minor features — compact list */}
      {minor.length > 0 && (
        <FadeIn delay={0.15}>
          <div className="mt-6 rounded-xl border border-border/60 bg-muted/20 px-5 py-1">
            <p className="mb-1 mt-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
              Also includes
            </p>
            <div className="grid sm:grid-cols-2">
              {minor.map((item) => (
                <MinorFeatureRow key={item.title} {...item} />
              ))}
            </div>
          </div>
        </FadeIn>
      )}
    </section>
  );
}
